import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, FileSpreadsheet, FileArchive, Download, Loader2, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError, fmtDate, gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COLUMNS = ["recipient_name", "recipient_email", "recipient_phone", "line1", "line2", "line3", "city", "postcode", "file", "subject", "category", "require_id_check"];
const SAMPLE = ["Jane Smith", "jane@example.com", "+447700900123", "Flat 2", "10 High Street", "", "Leeds", "LS1 1AA", "jane-smith.pdf", "", "general", "no"];

export const BATCH_STYLES = {
  processing: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  awaiting_payment: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  sending: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  sent: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  failed: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
};
export const BatchStatus = ({ status, testId }) => (
  <span data-testid={testId} className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${BATCH_STYLES[status] || ""}`}>{status?.replace("_", " ")}</span>
);

export const downloadTemplate = () => {
  const csv = `${COLUMNS.join(",")}\n${SAMPLE.map((v) => (v.includes(",") ? `"${v}"` : v)).join(",")}\n`;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  Object.assign(document.createElement("a"), { href: url, download: "postalcloud-bulk-template.csv" }).click();
  URL.revokeObjectURL(url);
};

const FilePick = ({ id, label, accept, icon: Icon, file, onPick }) => (
  <label htmlFor={id} className="flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-5 cursor-pointer hover:border-emerald-500 transition-colors">
    <Icon size={22} className="text-emerald-600 shrink-0" />
    <div className="min-w-0"><p className="text-sm font-semibold">{label}</p><p className="text-xs text-slate-500 truncate">{file ? file.name : "Choose file"}</p></div>
    <input id={id} data-testid={`${id}-input`} type="file" accept={accept} className="sr-only" onChange={(e) => onPick(e.target.files[0] || null)} />
  </label>
);

const UploadForm = ({ unit }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", subject: "", csv: null, zip: null });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [phoneOnly, setPhoneOnly] = useState(false);
  const submit = async (e, skipBadPhones = false) => {
    e?.preventDefault();
    if (!f.csv || !f.zip) return toast.error("Add both the CSV and the ZIP of letters");
    setBusy(true);
    setErrors([]);
    setPhoneOnly(false);
    try {
      const fd = new FormData();
      fd.append("csv_file", f.csv);
      fd.append("zip_file", f.zip);
      fd.append("name", f.name);
      fd.append("subject", f.subject);
      if (skipBadPhones) fd.append("skip_bad_phones", "true");
      const { data } = await api.post("/bulk/batches", fd);
      const dropped = data.phones_removed_rows?.length;
      toast.success(`Batch ${data.reference} created with ${data.count} letters${dropped ? ` (${dropped} without a mobile number)` : ""}`);
      nav(`/app/bulk/${data.id}`);
    } catch (err) {
      setErrors(err?.response?.data?.detail?.errors || []);
      setPhoneOnly(!!err?.response?.data?.detail?.phone_only);
      toast.error(formatError(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="bulk-upload-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label htmlFor="b-name">Batch name</Label><Input id="b-name" data-testid="bulk-name-input" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. October council tax notices" className="h-11" /></div>
        <div className="space-y-2"><Label htmlFor="b-subject">Default subject</Label><Input id="b-subject" data-testid="bulk-subject-input" required minLength={2} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Used when a row has no subject" className="h-11" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <FilePick id="bulk-csv" label="Recipients CSV (max 1,000 rows)" accept=".csv,text/csv" icon={FileSpreadsheet} file={f.csv} onPick={(v) => setF({ ...f, csv: v })} />
        <FilePick id="bulk-zip" label="ZIP of PDF letters (max 100 MB)" accept=".zip,application/zip" icon={FileArchive} file={f.zip} onPick={(v) => setF({ ...f, zip: v })} />
      </div>
      {!!errors.length && (
        <div data-testid="bulk-errors" className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-4 max-h-64 overflow-auto">
          <p className="text-sm font-semibold text-red-700 flex items-center gap-2"><AlertTriangle size={15} /> Fix these rows and upload again. Nothing has been sent.</p>
          <ul className="mt-2 space-y-1 text-xs text-red-700 font-mono">{errors.map((e, i) => <li key={i}>{e.row ? `Row ${e.row}: ` : ""}{e.error}</li>)}</ul>
          {phoneOnly && (
            <div className="mt-4 pt-3 border-t border-red-200 dark:border-red-900 flex flex-wrap items-center gap-3">
              <button type="button" data-testid="bulk-skip-bad-phones-btn" disabled={busy} onClick={() => submit(null, true)} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Send these rows without a mobile number</button>
              <p className="text-xs text-red-700">Those recipients still get their letter by email. They just won't get text codes.</p>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">Your rate: <span className="font-semibold text-slate-900 dark:text-white" data-testid="bulk-unit-price">{gbp(unit)}</span> per letter, VAT included. All rows are checked before anything is stored or charged.</p>
        <button data-testid="bulk-submit-btn" type="submit" disabled={busy} className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Validate & create batch
        </button>
      </div>
    </form>
  );
};

const BatchList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/bulk/batches").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loader2 className="animate-spin text-emerald-600" />;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="bulk-empty">No batches yet.</p>;
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="bulk-batch-list">
      {rows.map((b) => (
        <Link key={b.id} to={`/app/bulk/${b.id}`} data-testid={`bulk-batch-${b.reference}`} className="flex flex-wrap items-center justify-between gap-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 px-2 rounded-lg transition-colors">
          <div><p className="font-semibold text-sm">{b.name}</p><p className="text-xs text-slate-500 font-mono">{b.reference} · {fmtDate(b.created_at)}</p></div>
          <div className="flex items-center gap-4 text-sm"><span>{b.count} letters · {gbp(b.total_gbp)}</span><BatchStatus status={b.status} /></div>
        </Link>
      ))}
    </div>
  );
};

export default function Bulk() {
  const { user } = useAuth();
  if (!user?.enterprise) {
    return (
      <div data-testid="bulk-locked" className="max-w-xl">
        <PageHeader eyebrow="Enterprise" title="Bulk send" />
        <Panel><p className="flex gap-2 text-sm"><Lock size={16} className="text-emerald-600 shrink-0" /> Bulk sending is available to approved Enterprise accounts.</p>
          <Link to="/enterprise#enquire" className="inline-flex mt-4 underline text-sm">Request Enterprise access</Link></Panel>
      </div>
    );
  }
  return (
    <div data-testid="bulk-page" className="max-w-5xl space-y-6">
      <PageHeader eyebrow="Enterprise" title="Bulk send">
        <button data-testid="bulk-template-btn" onClick={downloadTemplate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><Download size={15} /> CSV template</button>
      </PageHeader>
      <Panel title="New batch">
        {!(user.org?.perms || []).includes("batches:send") ? <p data-testid="bulk-readonly" className="text-sm text-slate-500">Your team role is read-only. You can view batches and download reports.</p> : <>
        <p className="text-sm text-slate-500 mb-5">One CSV row per recipient. The <span className="font-mono">file</span> column names that recipient's PDF inside the ZIP. Required: recipient_name, recipient_email, line1, city, postcode, file. Optional: recipient_phone (+44…), line2, line3, subject.</p>
        <UploadForm unit={user.unit_price_gbp} /></>}
      </Panel>
      <Panel title="Your batches"><BatchList /></Panel>
    </div>
  );
}
